import { type FeatureFlagKey, featureFlags } from './flags';
import { clearAllFeatureFlags, setFeatureFlags } from './cookies';

const FLAG_QUERY_PARAM_PREFIX = 'ff_';
const TRUE_BOOLEAN_VALUES = new Set(['', '1', 'true', 'on', 'yes']);
const FALSE_BOOLEAN_VALUES = new Set(['0', 'false', 'off', 'no']);

function parseBooleanFlagValue(value: string | null): boolean | undefined {
  const normalized = (value ?? '').trim().toLowerCase();

  if (TRUE_BOOLEAN_VALUES.has(normalized)) return true;
  if (FALSE_BOOLEAN_VALUES.has(normalized)) return false;
  return undefined;
}

/**
 * Process feature flag query parameters and apply them
 *
 * Supports URL parameters for controlling flags:
 * - ?enableFeatureFlag=flag1,flag2 - Enable one or more flags
 * - ?disableFeatureFlag=flag1,flag2 - Disable one or more flags
 * - ?ff_flag-name=true - Set a flag directly using the cookie-style prefix
 * - ?clearFeatureFlags=true - Clear all flags to defaults
 *
 * After processing, removes the flag query parameters from the URL using replaceState
 * (keeps other query parameters intact)
 */
export function processFeatureFlagsFromURL(): void {
  if (typeof window === 'undefined' || typeof URLSearchParams === 'undefined') {
    return;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const flagsToUpdate: Partial<Record<FeatureFlagKey, string | boolean>> = {};
  const directFlagParamsToDelete = new Set<string>();
  let hasChanges = false;

  // Check for clearFeatureFlags parameter
  if (searchParams.has('clearFeatureFlags')) {
    // Strip the flag params before clearing. clearAllFeatureFlags() reloads, and
    // if clearFeatureFlags is still in the URL the reloaded page would clear and
    // reload again — an endless loop.
    searchParams.delete('enableFeatureFlag');
    searchParams.delete('disableFeatureFlag');
    searchParams.delete('clearFeatureFlags');
    const clearedSearch = searchParams.toString();
    const clearedURL = clearedSearch ? `${window.location.pathname}?${clearedSearch}` : window.location.pathname;
    window.history.replaceState(null, '', clearedURL);

    clearAllFeatureFlags();
    return; // clearAllFeatureFlags calls window.location.reload()
  }

  // Get valid flag keys
  const validFlagKeys = new Set(featureFlags.map((f) => f.key));

  // Process enableFeatureFlag parameter (comma-separated)
  if (searchParams.has('enableFeatureFlag')) {
    const enableStr = searchParams.get('enableFeatureFlag');
    if (enableStr) {
      const flagsToEnable = enableStr.split(',').map((f) => f.trim());
      for (const flagKey of flagsToEnable) {
        if (validFlagKeys.has(flagKey as FeatureFlagKey)) {
          const flagDef = featureFlags.find((f) => f.key === flagKey);
          if (flagDef) {
            flagsToUpdate[flagKey as FeatureFlagKey] = flagDef.type === 'boolean' ? true : flagDef.defaultValue;
            hasChanges = true;
          }
        }
      }
    }
  }

  // Process disableFeatureFlag parameter (comma-separated)
  if (searchParams.has('disableFeatureFlag')) {
    const disableStr = searchParams.get('disableFeatureFlag');
    if (disableStr) {
      const flagsToDisable = disableStr.split(',').map((f) => f.trim());
      for (const flagKey of flagsToDisable) {
        if (validFlagKeys.has(flagKey as FeatureFlagKey)) {
          const flagDef = featureFlags.find((f) => f.key === flagKey);
          if (flagDef) {
            flagsToUpdate[flagKey as FeatureFlagKey] = flagDef.defaultValue;
            hasChanges = true;
          }
        }
      }
    }
  }

  for (const [paramName, rawValue] of searchParams.entries()) {
    if (!paramName.startsWith(FLAG_QUERY_PARAM_PREFIX)) continue;

    const flagKey = paramName.slice(FLAG_QUERY_PARAM_PREFIX.length);
    directFlagParamsToDelete.add(paramName);

    if (!validFlagKeys.has(flagKey as FeatureFlagKey)) continue;

    const flagDef = featureFlags.find((f) => f.key === flagKey);
    if (!flagDef) continue;

    if (flagDef.type === 'boolean') {
      const parsedValue = parseBooleanFlagValue(rawValue);
      if (parsedValue === undefined) continue;

      flagsToUpdate[flagKey as FeatureFlagKey] = parsedValue;
      hasChanges = true;
      continue;
    }

    if (flagDef.variants.includes(rawValue as never)) {
      flagsToUpdate[flagKey as FeatureFlagKey] = rawValue;
      hasChanges = true;
    }
  }

  if (!hasChanges) {
    return;
  }

  // Remove flag-related query parameters from URL
  searchParams.delete('enableFeatureFlag');
  searchParams.delete('disableFeatureFlag');
  searchParams.delete('clearFeatureFlags');
  directFlagParamsToDelete.forEach((paramName) => searchParams.delete(paramName));

  const newSearch = searchParams.toString();
  const newURL = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;
  window.history.replaceState(null, '', newURL);

  // Apply flag changes
  setFeatureFlags(flagsToUpdate);
}
